import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { RoleCategory } from '@hcc/types';

/**
 * The role-categories master list (the groupings job roles fall under, e.g.
 * Engineer / Office / Manager). `useRoleCategories()` returns active ones for
 * the pickers; the admin card uses `useAllRoleCategories` to manage all.
 */

const KEY = ['role-categories'] as const;
const ALL_KEY = ['role-categories', 'all'] as const;

export function useRoleCategories() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api<{ categories: RoleCategory[] }>('/api/admin/role-categories')).categories,
  });
}

export function useAllRoleCategories() {
  return useQuery({
    queryKey: ALL_KEY,
    queryFn: async () =>
      (
        await api<{ categories: RoleCategory[] }>(
          '/api/admin/role-categories?include_archived=true',
        )
      ).categories,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: KEY });
  qc.invalidateQueries({ queryKey: ALL_KEY });
  // Roles embed category_name, so a rename/archive changes their display too.
  qc.invalidateQueries({ queryKey: ['roles'] });
}

export function useCreateRoleCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api<{ category: RoleCategory }>('/api/admin/role-categories', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateRoleCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<{ category: RoleCategory }>(`/api/admin/role-categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useArchiveRoleCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ category: RoleCategory }>(`/api/admin/role-categories/${id}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useRestoreRoleCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ category: RoleCategory }>(`/api/admin/role-categories/${id}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => invalidate(qc),
  });
}
